import * as d3 from 'd3';
import {
  sankey as SankeyLib,
  sankeyJustify as SankeyLibSankeyJustify,
  sankeyLinkHorizontal as SankeyLinkHorizontalLib
} from 'd3-sankey';

const CLASSNAME_UNIT_WRAPPER = 'wrapper-unit';
const CLASSNAME_TEXT_VALUE = 'text-value';
const CLASSNAME_LINE_VALUE = 'line-value';
const CLASSNAME_Y_LABEL = 'y-label';
const TEXT_FONT_SIZE = 12;

function showUnit(unitWrapper) {
  unitWrapper.style('opacity', 1);
}
function hideUnit(unitWrapper) {
  unitWrapper.style('opacity', 0);
}
function showNodeValue(template, gNode, value, actualValue, format) {
  const nodeValue = template.replace(/(\$\{value\})/ig, format(actualValue))
    .replace(/(\$\{percent\})/ig, d3.format(',.1f')(value));
  gNode.select(`.${CLASSNAME_TEXT_VALUE}`)
    .style('opacity', 1)
    .text(nodeValue);
  gNode.select(`.${CLASSNAME_LINE_VALUE}`).style('opacity', 1);
  d3.select(gNode.node().closest('.wrapper')).select(`.${CLASSNAME_Y_LABEL}`)
    .style('opacity', 0);
}
function hideNodeValue(gNode) {
  gNode.select(`.${CLASSNAME_TEXT_VALUE}`)
    .style('opacity', 0)
    .text(null);
  gNode.select(`.${CLASSNAME_LINE_VALUE}`).style('opacity', 0);
  d3.select(gNode.node().closest('.wrapper')).select(`.${CLASSNAME_Y_LABEL}`)
    .style('opacity', 1);
}

export function Sankey(settings) {
  const _settings = Object.assign({
    $el: d3.select('body'),
    data: [],
    width: 960,
    height: 500,
    margin: { top: 20, right: 0, bottom: 20, left: 0, },
    nodeWidth: 20,
    linkColor: '#CDCDCD',
    linkHoveredColor:'rgb(189, 212, 231)', 
    linkOpacity: 0.5,
    linkHoveredOpacity: 0.9,
    linkValueTemplate: '${value} (${percent}%)',
    percentField: 'value',
    valueField: 'actual_value',
    color: d3.scaleOrdinal(d3.schemeCategory10),
    format: d3.format(',.0f'),
    onClick: null,
    onHover: null,
  }, settings);

  var self = this;
  self.isLocked = false;
  Object.keys(_settings).forEach(function(k) {
    self[k] = _settings[k];
  });

  this.render();
}

Sankey.prototype.render = function() {
  const self = this;
  const names = this.data.nodes.map(d => d.name);
  const maxCountCharNames = Math.max(...names.map(d => d.length));
  const charWidth = 5;
  const nameWidth = maxCountCharNames * charWidth;
  const valueWidth = 25;
  const unitHeight = this.unitName !== undefined ? 20 : 0;
  const chartPadding = {
    top: 0 + unitHeight,
    left: 80 + nameWidth + valueWidth, 
    bottom: 0,
    right: 80 + nameWidth + valueWidth,
  };

  const width = this.width + this.margin.left + this.margin.right;
  const height = this.height + this.margin.top + this.margin.bottom;

  this.$el.selectAll('*').remove();
  const svg = this.$el.append('svg')
    .attr('width', width)
    .attr('height', height)
    .attr('transform', `translate(${this.margin.left}, ${this.margin.top})`);

  const gWrapper = svg.append('g')
    .attr('class', 'wrapper')
    .attr('transform', `translate(0, 0)`);

  if (this.yLabel !== undefined) {
    gWrapper.append('text')
      .attr('class', CLASSNAME_Y_LABEL)
      .attr('x', -(this.height / 2))
      .attr('y', this.margin.top)
      .style('transform', 'rotate(-90deg)')
      .text(this.yLabel)
  }
  if (this.nodeColLabels !== undefined) {
    const y = this.height + this.margin.top;
    gWrapper.append('text')
      .attr('class', 'node-col-label left')
      .attr('x', chartPadding.left)
      .attr('y', y)
      .style('font-weight', 'bold')
      .text(this.nodeColLabels[0])
    gWrapper.append('text')
      .attr('class', 'node-col-label right')
      .attr('text-anchor', 'end')
      .attr('x', this.width - chartPadding.right)
      .attr('y', y)
      .style('font-weight', 'bold')
      .text(this.nodeColLabels[1])
  }

  let unitWrapper = undefined
  if (this.unitName !== undefined) {
    unitWrapper = gWrapper
      .append('g')
      .attr('class', CLASSNAME_UNIT_WRAPPER)
      .attr('transform', `translate(0, ${this.margin.top})`)
      .style('height', unitHeight)
      .style('opacity', 0);
    unitWrapper.append('text')
      .attr('class', 'unit-left')
      .attr('transform', `translate(${chartPadding.left / 2}, 0)`)
      .style('font-size', TEXT_FONT_SIZE)
      .text(this.unitName);
    unitWrapper
      .attr('class', 'unit-right').append('text')
      .attr('transform', `translate(${(width - (chartPadding.right / 2))}, 0)`)
      .style('font-size', TEXT_FONT_SIZE)
      .text(this.unitName);
  }

  const chartWidth = this.width - chartPadding.left - chartPadding.right;
  const chartHeight = this.height - chartPadding.top - chartPadding.bottom;

  // Set the sankey diagram properties
  const sankey = SankeyLib()
    .nodeWidth(this.nodeWidth)
    // .nodePadding(40)
    .nodeSort(null)
    .nodeAlign(SankeyLibSankeyJustify)
    .iterations(0)
    .size([chartWidth, chartHeight]);

  const graph = sankey(this.data);

  const chartWrapper = gWrapper.append('g')
    .attr('class', 'wrapper-chart')
    .attr('transform', `translate(${chartPadding.left}, ${chartPadding.top})`);

  // add in the links
  const linkWrapper = chartWrapper.append('g').attr('class', 'wrapper-link');
  linkWrapper
    .selectAll('.link')
    .data(graph.links)
    .enter()
    .append('path')
      .attr('class', 'link')
      .attr('d', function(d) {
        return SankeyLinkHorizontalLib()(d); })
      .attr('stroke-width', function(d) {
        return d.width; })
      .attr('data-link-index', function(d) {
        return d.index; })
      .style('fill', 'none')
      .style('stroke', this.linkColor)
      .style('stroke-opacity', this.linkOpacity)
      .on('mouseover', function() {
        if (self.isLocked) return;

        const $this = d3.select(this);
        $this.style('stroke', self.linkHoveredColor)
          .style('stroke-opacity', self.linkHoveredOpacity);

        const link = $this.datum();
        const sourceNode = link.source;
        const sourceLink = sourceNode.sourceLinks.find(e => e.index === link.index);
        const $sourceNode = chartWrapper.select(`.node[data-node-index="${sourceNode.index}"]`);
        showNodeValue(self.linkValueTemplate, $sourceNode, sourceLink[self.percentField],
          sourceLink[self.valueField], self.format);

        const targetNode = link.target;
        const targetLink = targetNode.targetLinks.find(e => e.index === link.index);
        const $targetNode = chartWrapper.select(`.node[data-node-index="${targetNode.index}"]`);
        showNodeValue(self.linkValueTemplate, $targetNode, targetLink[self.percentField],
          targetLink[self.valueField], self.format);
      })
      .on('mouseout', function() {
        if (self.isLocked) return;

        const $this = d3.select(this);
        $this.style('stroke', self.linkColor)
          .style('stroke-opacity', self.linkOpacity);

        const link = $this.datum();
        const sourceNode = link.source;
        const $sourceNode = chartWrapper.select(`.node[data-node-index="${sourceNode.index}"]`);
        hideNodeValue($sourceNode);

        const targetNode = link.target;
        const $targetNode = chartWrapper.select(`.node[data-node-index="${targetNode.index}"]`);
        hideNodeValue($targetNode);
      });
      // .sort(function(a, b) {
      //   return b.dy - a.dy;
      // });

  // add in the nodes
  const nodeWrapper = chartWrapper.append('g').attr('class', 'wrapper-node');
  const gNode = nodeWrapper
    .selectAll('.node')
    .data(graph.nodes)
    .enter()
    .append('g')
      .attr('class', 'node')
      .attr('data-node-index', function(d) {
        return d.index; });
      // .attr('transform', function(d) {
      //   return `translate(${d.x}, ${d.y})`;
      // })
      // .call(d3.drag()
      //   .subject(function(d) { return d; })
      //   .on("start", function() { this.parentNode.appendChild(this); })
      //   .on("drag", dragmove));

  // add the rectangles for the nodes
  gNode.append('rect')
    .attr('x', function(d) { return d.x0; })
    .attr('y', function(d) {
      return d.y0; })
    .attr('height', function(d) { return d.y1 - d.y0; })
    .attr('width', sankey.nodeWidth())
    .style('fill', function(d) {
      return d.color = self.color(d.name); })
    .on('mouseover', function(e) {
      if (self.isLocked) return;

      if (unitWrapper !== undefined) showUnit(unitWrapper);

      const $this = d3.select(this);
      const datum = $this.datum();
      const links = [ ...datum.sourceLinks, ...datum.targetLinks, ];
      const $parent = d3.select(this.parentNode);
      const actualValue = links.reduce((prev, curr) => prev + curr[self.valueField], 0);
      showNodeValue(self.linkValueTemplate, $parent, datum[self.percentField], actualValue, self.format);
      const isLeftColumn = datum.depth === 0;
      for (const link of links) {
        const source = isLeftColumn ? link.target : link.source;
        const partialLinks = isLeftColumn ? source.targetLinks : source.sourceLinks;
        const partialLink = partialLinks.find(e => e.index === link.index);
        const nodeIndex = isLeftColumn ? link.target.node : link.source.node;
        const $gNode = chartWrapper.select(`.node[data-node-index="${nodeIndex}"]`);
        showNodeValue(self.linkValueTemplate, $gNode, partialLink[self.percentField],
          partialLink[self.valueField], self.format);
        chartWrapper.select(`.link[data-link-index="${link.index}"]`)
          .style('stroke', datum.color)
          .style('stroke-opacity', self.linkHoveredOpacity);
      }
      if (self.onHover === undefined) return;
      self.onHover({ key: datum.name, value: datum[self.percentField], });
    })
    .on('mouseout', function(e) {
      if (self.isLocked) return;

      if (unitWrapper !== undefined) hideUnit(unitWrapper);

      const $this = d3.select(this);
      const datum = $this.datum();
      const links = [ ...datum.sourceLinks, ...datum.targetLinks, ];
      const $parent = d3.select(this.parentNode);
      hideNodeValue($parent);
      const isLeftColumn = datum.depth === 0;
      for (const link of links) {
        const nodeIndex = isLeftColumn ? link.target.node : link.source.node;
        const $gNode = chartWrapper.select(`.node[data-node-index="${nodeIndex}"]`);
        hideNodeValue($gNode);
        chartWrapper.select(`.link[data-link-index="${link.index}"]`)
          .style('stroke', self.linkColor)
          .style('stroke-opacity', self.linkOpacity);
      } })
    .on('click', function(e) {
      if (self.onClick === undefined) return;

      self.isLocked = !self.isLocked;
      const $this = d3.select(this);
      const datum = $this.datum();
      self.onClick({
        key: datum.name,
        value: datum[self.percentField],
      });
     })
    .append('title')
      .text(function(d) {
        return d.name + "\n" + self.format(d[self.percentField]); });

  // add in the title for the nodes
  const text = gNode.append('text')
    .style('font-size', TEXT_FONT_SIZE)
    .attr('dy', '0.35em')
    .attr('y', function(d) { return (d.y1 + d.y0) / 2; });
  text.filter(function(d) {
      return d.depth === 0; }) // Filter left column nodes
    .attr('text-anchor', 'end')
    .attr('x', function(d) { return d.x0 - 6; });
  text.filter(function(d) {
      return d.depth === 1; }) // Filter right column nodes
    .attr('text-anchor', 'start')
    .attr('x', function(d) { return d.x1 + 6; })

  text.append('tspan')
    .attr('class', 'text-name')
    .text(function(d) { return d.name; });

  const textValue = text.append('tspan')
    .attr('class', CLASSNAME_TEXT_VALUE)
    .style('opacity', 0);
  textValue.filter(function(d) { // Filter left column nodes
    return d.sourceLinks.length > 0; })
    .attr('text-anchor', 'start')
    .attr('x', -chartPadding.left);
  textValue.filter(function(d) { // Filter right column nodes
    return d.targetLinks.length > 0; })
    .attr('text-anchor', 'end')
    .attr('x', function(d) {
      return d.x1 + chartPadding.right; })

  const lineHSpacing = 6;
  const lineVSpacing = TEXT_FONT_SIZE / 2;
  gNode.append('line')
    .attr('class', CLASSNAME_LINE_VALUE)
    .attr('x1', -chartPadding.left)
    .attr('x2', function(d) {
      return d.x1 - sankey.nodeWidth() - lineHSpacing; })
    .attr('y1', d => {
      return ((d.y1 + d.y0) / 2) + lineVSpacing;
    })
    .attr('y2', d => {
      return ((d.y1 + d.y0) / 2) + lineVSpacing;
    })
    .style('stroke-dasharray', '2,2')
    .style('stroke', 'gray')
    .style('stroke-width', 1)
    .style('opacity', 0)
    .filter(function(d) { // Filter right nodes
      // @note: use d.depth/d.targetLinks/d.sourceLinks to determind which column
      return d.targetLinks.length > 0;
    })
      .attr('x1', function(d) {
        return d.x1 + lineHSpacing; })
      .attr('x2', function(d) {
        return d.x1 + chartPadding.right; });
};
